import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { editEventAbout, listGatheringKinds, readEventAbout, type EventAboutPatch } from "@/api/event-about";
import { TownLookup } from "./TownLookup";

/**
 * What the event says about itself, and where it is held: the kind of
 * gathering, the last entry time, and the place with its visitor rules. Each
 * control is a partial edit (PATCH /events/{id}); an empty value sends null
 * and clears it.
 */
export function EventAboutPanel({ eventId }: { eventId: string }) {
  const qc = useQueryClient();
  const [msg, setMsg] = useState<string | null>(null);
  const about = useQuery({ queryKey: ["event-about", eventId], queryFn: () => readEventAbout(eventId) });
  const kinds = useQuery({ queryKey: ["gathering-kinds"], queryFn: listGatheringKinds });
  const event = about.data?.data;

  async function save(patch: EventAboutPatch) {
    setMsg(null);
    try {
      await editEventAbout(eventId, patch);
      setMsg("Saved.");
      await qc.invalidateQueries({ queryKey: ["event-about", eventId] });
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "That did not work");
    }
  }

  if (!event) return null;
  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium text-gray-700">About this event</h3>
      <label className="flex items-center gap-2 text-sm">
        <span className="w-40 text-gray-600">Kind of gathering</span>
        <select
          className="input w-72"
          value={event.gatheringKind ?? ""}
          onChange={(e) => void save({ gatheringKind: e.target.value || null })}
        >
          <option value="">Not said</option>
          {(kinds.data?.data.kinds ?? []).map((k) => (
            <option key={k.code} value={k.code}>
              {k.label}
              {k.worship ? " (worship)" : ""}
            </option>
          ))}
        </select>
      </label>
      <label className="flex items-center gap-2 text-sm">
        <span className="w-40 text-gray-600">Who can see it</span>
        <select
          className="input w-40"
          value={event.visibility}
          onChange={(e) => {
            const visibility = e.target.value as "public" | "link" | "circle";
            if (visibility !== "circle") return void save({ visibility });
            const circleId = window.prompt("The circle this event is for (circle id)")?.trim();
            if (circleId) void save({ visibility, circleId });
          }}
        >
          <option value="public">Everyone (listed)</option>
          <option value="link">People with the link</option>
          <option value="circle">One circle</option>
        </select>
        {event.circleId && <span className="text-xs text-gray-500">circle {event.circleId}</span>}
      </label>
      <label className="flex items-center gap-2 text-sm">
        <span className="w-40 text-gray-600">Street address shows to</span>
        <select
          className="input w-40"
          value={event.addressVisibility}
          onChange={(e) => void save({ addressVisibility: e.target.value as "everyone" | "going" })}
        >
          <option value="everyone">Everyone who can see it</option>
          <option value="going">People going</option>
        </select>
        <span className="text-xs text-gray-500">{event.addressShown ? "shown to you" : "kept from you"}</span>
      </label>
      <label className="flex items-center gap-2 text-sm">
        <span className="w-40 text-gray-600">Last entry (HH:MM)</span>
        <input
          className="input w-28"
          key={event.lastEntryTime ?? ""}
          defaultValue={event.lastEntryTime ?? ""}
          placeholder="22:30"
          onBlur={(e) => {
            const value = e.target.value.trim() || null;
            if (value !== event.lastEntryTime) void save({ lastEntryTime: value });
          }}
        />
      </label>
      <label className="flex items-center gap-2 text-sm">
        <span className="w-40 text-gray-600">Held at (institution id)</span>
        <input
          className="input w-80"
          key={event.heldAt?.id ?? ""}
          defaultValue={event.heldAt?.id ?? ""}
          placeholder="An active, verified institution"
          onBlur={(e) => {
            const value = e.target.value.trim() || null;
            if (value !== (event.heldAt?.id ?? null)) void save({ heldAtInstitutionId: value });
          }}
        />
      </label>
      {event.heldAt && (
        <div className="text-xs text-gray-600 pl-40">
          <p>
            {event.heldAt.name}, {event.heldAt.city} {event.heldAt.state}. The place's rules:
          </p>
          <ul className="list-disc pl-4">
            {event.heldAt.visitorRules.length === 0 && <li>None kept.</li>}
            {event.heldAt.visitorRules.map((r, i) => (
              <li key={i}>
                {r.topic}: {r.text}
              </li>
            ))}
          </ul>
        </div>
      )}
      <label className="flex items-center gap-2 text-sm">
        <span className="w-40 text-gray-600">Festival code</span>
        <input
          className="input w-64"
          key={event.festivalCode ?? ""}
          defaultValue={event.festivalCode ?? ""}
          placeholder="holi-2027"
          onBlur={(e) => {
            const value = e.target.value.trim() || null;
            if (value !== event.festivalCode) void save({ festivalCode: value });
          }}
        />
      </label>
      <TownLookup />
      {msg && <p className="text-xs text-gray-600">{msg}</p>}
    </div>
  );
}
